import { REVEALED } from "./password-reveal-event"
import { captureUiEvent } from "./tracking"
import { documentReady } from "./utils/dom"

// htmx posts the whole enclosing form (dist/htmx.js `#collectFormData`), which holds the
// current password on change-password and the address on registration. The rules read only
// these: the password and the names `similarity_attributes` (apps/customer/views.py) compares.
const SENT_FIELDS = new Set([
  "csrfmiddlewaretoken",
  "password",
  "first_name",
  "last_name",
  "email",
  "partnername",
])

function showCharsLeft(counter, password) {
  const left = Math.max(Number(counter.dataset.minLength) - password.length, 0)
  counter.hidden = left === 0
  counter.textContent =
    left === 1 ? counter.dataset.charsOne : counter.dataset.charsMany.replace("{count}", left)
}

function criterion(container, key) {
  return container.querySelector(`[data-criterion="${key}"]`)
}

function hasWords(container, key) {
  return Boolean(criterion(container, key)?.querySelector("[data-criterion-words] li"))
}

function trackBlur(container, secondsInField) {
  captureUiEvent("password_blurred", container.dataset.uiId, {
    requirements_fulfilled: container.querySelector(".pwd-validation--valid") !== null,
    has_required_length: criterion(container, "long-enough?")?.classList.contains("valid") ?? false,
    has_personal_info: hasWords(container, "similarity-personal"),
    has_business_info: hasWords(container, "similarity-business"),
    time_spent_in_field: secondsInField,
  })
}

function showNeutral(container, neutral, { error = false } = {}) {
  const panel = neutral.cloneNode(true)
  panel.querySelector("[data-password-error]").hidden = !error
  container.querySelector(".pwd-validation").replaceWith(panel)
}

function wire(container) {
  const input = container.querySelector("input[name='password']")
  const counter = container.querySelector("[data-password-chars-left]")
  const neutral = container.querySelector(".pwd-validation").cloneNode(true)
  let focusedAt = Date.now()
  let latestCheck = 0
  const isStale = ({ detail }) => detail.ctx != null && detail.ctx.passwordCheck !== latestCheck

  input.addEventListener("input", () => showCharsLeft(counter, input.value))
  input.addEventListener("focus", () => {
    focusedAt = Date.now()
  })
  input.addEventListener("blur", () => {
    trackBlur(container, Math.floor((Date.now() - focusedAt) / 10) / 100)
  })
  container.addEventListener(REVEALED, () => {
    captureUiEvent("password_revealed", container.dataset.uiId, {})
  })

  // An empty field has nothing to check, so editing a name before the password sends
  // nothing, and an answer still in flight cannot mark the list for a cleared field.
  container.addEventListener("htmx:config:request", (event) => {
    event.detail.ctx.passwordCheck = ++latestCheck
    if (!input.value) {
      event.preventDefault()
      container.dispatchEvent(new CustomEvent("htmx:abort"))
      showNeutral(container, neutral)
      return
    }
    const { body } = event.detail.ctx.request
    for (const name of [...body.keys()]) {
      if (!SENT_FIELDS.has(name)) {
        body.delete(name)
      }
    }
  })
  // htmx 4.0.0 `RequestQueue.continue()`, called from an aborted request's `finally`, frees the
  // slot of the request that replaced it, so the next request runs beside that one, and the
  // first answer to arrive takes the only attached `.pwd-validation`.
  container.addEventListener("htmx:after:request", (event) => {
    if (isStale(event)) {
      event.preventDefault()
    }
  })
  container.addEventListener("htmx:response:error", () => {
    showNeutral(container, neutral, { error: true })
  })
  container.addEventListener("htmx:error", (event) => {
    if (event.detail.error?.name !== "AbortError" && !isStale(event)) {
      showNeutral(container, neutral, { error: true })
    }
  })

  showCharsLeft(counter, input.value)
}

documentReady(() => {
  document.querySelectorAll("[data-password-strength]").forEach(wire)
})
